/**
 * The adaptive layout (decision 12; part 11 log, table Y; part 15, rows Y6–Y10). Wide screens (desktop, landscape
 * tablet) show the chapter list beside the editor and the preview beside the text; narrow ones (phone, portrait tablet)
 * show one pane at a time, the list as a drawer over the editor and the preview in the text's place, switched with a
 * toggle or a swipe. Focus mode shows the text alone. A preview that isn't shown does no work.
 */

import type { WriterLayout } from './settings.ts';

/** Below this CSS width the screen is narrow (Y1): a portrait tablet (820 px) and a landscape phone (844 px) are. */
export const WIDE_MIN_WIDTH = 1000;

/** How the screen is used (Y1). */
export type LayoutMode = 'wide' | 'narrow';

/** The mode for a viewport `width` CSS pixels wide (Y1). */
export function layoutMode(width: number): LayoutMode {
  return width >= WIDE_MIN_WIDTH ? 'wide' : 'narrow';
}

/** Which of the text and the preview a narrow screen shows (Y8, Y9). */
export type NarrowPane = 'text' | 'preview';

/** What the writer chose that decides the panes (Y2–Y9; part 11d, R4). */
export interface PaneInput {
  /** Part 11d, R4: whether the writer is the page shown; on any other page (home, settings) no pane of it shows. */
  readonly writing: boolean;
  readonly mode: LayoutMode;
  readonly focusMode: boolean;
  /** Whether the writer has the chapter list open: the sidebar when wide, the drawer when narrow. */
  readonly listOpen: boolean;
  /** What a wide screen shows (the device setting; part 11f, WL1–WL3): text and pages, pages alone, or text alone. */
  readonly layout: WriterLayout;
  /** What a narrow screen shows. */
  readonly narrowPane: NarrowPane;
  /** Part 19e, D′1: whether the Notes panel is open. */
  readonly notesOpen: boolean;
}

/** Which panes show (Y2–Y9). */
export interface Panes {
  /** The chapter list beside the editor. */
  readonly sidebar: boolean;
  /** The chapter list over the editor, which it hides from assistive technology while open. */
  readonly drawer: boolean;
  /** The toolbar above the editor. */
  readonly toolbar: boolean;
  /** The text; a narrow screen showing the preview keeps it in the page, hidden, with its typing. */
  readonly editor: boolean;
  /** The preview; only a shown one lays out or draws anything (decision 12). */
  readonly preview: boolean;
  /** Part 19e, D′1, D′10: the Notes panel: beside the text on a wide screen (in the pages' column), a sheet on a narrow one. */
  readonly notes: boolean;
  /**
   * Part 11d, R4: the preview's column, held empty while another page covers the writer, so the text keeps its width and
   * its layout (decision 14: Continue writing to typing) while the preview does no work.
   */
  readonly previewSpace: boolean;
}

/**
 * The panes for `input` (table Y): focus mode shows only the text (Y4), whatever else is open; wide shows the preview
 * beside the text when the writer has it on (Y6, Y7); narrow shows the text or the preview (Y8, Y9). Under another page
 * (part 11d, R4) the writer keeps its layout, laid out and covered, but its preview does no work and no drawer opens.
 */
export function panes(input: PaneInput): Panes {
  const wide = input.mode === 'wide';
  // Part 19e, D′1: on a wide screen the open notes take the pages' column, and the text shows beside them whatever the layout.
  const notes = !input.focusMode && input.notesOpen;
  const besideNotes = wide && notes;
  const preview = !input.focusMode && !besideNotes && (wide ? input.layout !== 'text' : input.narrowPane === 'preview');
  const shown = input.focusMode
    ? { sidebar: false, drawer: false, toolbar: false, editor: true }
    : { sidebar: wide && input.listOpen, drawer: !wide && input.listOpen, toolbar: true, editor: wide ? besideNotes || input.layout !== 'pages' : !preview };
  return input.writing ? { ...shown, preview, notes, previewSpace: false } : { ...shown, drawer: false, preview: false, notes: false, previewSpace: preview };
}
